import React, { useState, useMemo, useEffect } from 'react';
import PopoverContainer from 'carbon-react/esm/components/popover-container';
import Button from 'carbon-react/esm/components/button/__next__';
import Box from 'carbon-react/esm/components/box';
import { MultiSelect, OptionRow } from 'carbon-react/esm/components/select';
import FilterNature from './FilterNature';
import { useUsers } from '../../../hooks/useUsers';
import Badge from 'carbon-react/esm/components/badge';
import FilterPopoverProps from '../types/filterpopover.types';

// Modifiez les types dans les props et les états pour utiliser un tableau d'ID utilisateurs
const FilterPopover = ({
  selectedUserIdState,
  selectedNatures,
  onSubmitFilters,
}: FilterPopoverProps) => {
  const [isPopOverOpen, setIsPopOverOpen] = useState(false);
  // Modifiez l'état pour stocker un tableau d'IDs
  const [localSelectedUserIds, setLocalSelectedUserIds] = useState<string[]>(
    Array.isArray(selectedUserIdState) ? selectedUserIdState : [],
  );
  const [localSelectedNatures, setLocalSelectedNatures] = useState(selectedNatures);
  const [selectedUsers, setSelectedUsers] = useState<string[]>(
    Array.isArray(selectedUserIdState) ? selectedUserIdState : [],
  );
  const [activeUserFilter, setActiveUserFilter] = useState<string[] | null>(
    Array.isArray(selectedUserIdState) && selectedUserIdState.length > 0
      ? selectedUserIdState
      : null,
  );
  const [activeNatureFilters, setActiveNatureFilters] = useState<string[]>(selectedNatures);

  const { users: unsortedUsers } = useUsers();

  const users = useMemo(
    () => [...unsortedUsers].sort((a, b) => a.email.localeCompare(b.email)),
    [unsortedUsers],
  );

  const filteredUsers = useMemo(() => users, [users]);

  const handlePopoverClose = () => {
    if (isPopOverOpen) {
      setIsPopOverOpen(false);

      setLocalSelectedUserIds(activeUserFilter || []);
      setLocalSelectedNatures([...activeNatureFilters]);
      setSelectedUsers(activeUserFilter || []);
    }
  };

  useEffect(() => {
    const ids = Array.isArray(selectedUserIdState) ? selectedUserIdState : [];
    setActiveUserFilter(ids.length > 0 ? ids : null);
    setActiveNatureFilters(selectedNatures);

    setLocalSelectedUserIds(ids);
    setLocalSelectedNatures(selectedNatures);
    setSelectedUsers(ids);
  }, [selectedUserIdState, selectedNatures]);

  const handleUserSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedValues = event.target.value;
    if (Array.isArray(selectedValues)) {
      setSelectedUsers(selectedValues);
      setLocalSelectedUserIds(selectedValues);
    }
  };

  const handleLocalNatureChange = (natures: string[]) => {
    setLocalSelectedNatures(natures);
  };

  const handleCancel = () => {
    // Clear local state
    setLocalSelectedUserIds([]);
    setLocalSelectedNatures([]);
    setSelectedUsers([]);

    // Clear active filters
    setActiveUserFilter(null);
    setActiveNatureFilters([]);

    // Submit empty filters to clear localStorage
    onSubmitFilters([], []);

    setIsPopOverOpen(false);
  };

  const handleSubmit = () => {
    onSubmitFilters(localSelectedUserIds, localSelectedNatures);

    setActiveUserFilter(localSelectedUserIds);
    setActiveNatureFilters([...localSelectedNatures]);

    setIsPopOverOpen(false);
  };

  const getActiveFiltersCount = () => {
    const userFiltersCount =
      activeUserFilter && activeUserFilter.length > 0 ? activeUserFilter.length : 0;
    const natureFiltersCount = activeNatureFilters.length;
    return userFiltersCount + natureFiltersCount;
  };

  const natureProps = {
    placeholder: 'Rechercher une nature',
    label: 'Nature',
    onNatureChange: handleLocalNatureChange,
    selectedNatures: localSelectedNatures,
  };

  return (
    <PopoverContainer
      p={0}
      open={isPopOverOpen}
      onOpen={() => setIsPopOverOpen(true)}
      onClose={handlePopoverClose}
      renderCloseComponent={() => <span style={{ display: 'none' }} />}
      renderOpenComponent={({ ref: _ref, ...props }) => {
        const filterCount = getActiveFiltersCount();
        return filterCount > 0 ? (
          <Badge counter={filterCount}>
            <Button
              variantType="secondary"
              iconType={isPopOverOpen ? 'close' : 'filter_new'}
              iconPosition="before"
              {...props}
            >
              Filtres
            </Button>
          </Badge>
        ) : (
          <Button
            variantType="secondary"
            iconType={isPopOverOpen ? 'close' : 'filter_new'}
            iconPosition="before"
            {...props}
          >
            Filtres
          </Button>
        );
      }}
    >
      <Box width="430px" height="auto" p="16px 24px" display="flex" flexDirection="column">
        <Box height="100%" display="flex" flexDirection="column" gap="20px">
          <MultiSelect
            name="userList"
            id="userList"
            label="Utilisateur"
            placeholder="Rechercher un utilisateur"
            value={selectedUsers}
            onChange={handleUserSelect}
            multiColumn
            tableHeader={
              <tr>
                <th>Nom</th>
                <th>Email</th>
              </tr>
            }
          >
            {filteredUsers.map(user => (
              <OptionRow
                key={user.id}
                id={user.id}
                value={user.id}
                text={user.firstName + ' ' + user.lastName}
              >
                <td
                  style={{
                    maxWidth: '200px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {user.firstName + ' ' + user.lastName}
                </td>
                <td
                  style={{
                    maxWidth: '200px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {user.email}
                </td>
              </OptionRow>
            ))}
          </MultiSelect>

          <FilterNature {...natureProps} />
        </Box>

        <Box
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          paddingTop={5}
          gap={5}
        >
          <Button fullWidth onClick={handleCancel}>
            Réinitialiser les filtres
          </Button>
          <Button fullWidth variantType="primary" onClick={handleSubmit}>
            Appliquer les filtres
          </Button>
        </Box>
      </Box>
    </PopoverContainer>
  );
};

export default FilterPopover;
